import { Box, ButtonBase, Card, CardContent, Chip, Stack } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useEffect, useMemo, useRef } from 'react';
import { useDraggable } from '@dnd-kit/core';
import CodeSnippetView from './CodeSnippetView';
import type {
  CodeSnippetCodeByDropZone,
  CodeSnippetDropZone,
  CodeSnippetDropZoneMode,
  CodeSnippetImport,
  CodeSnippetInsertionOutcome,
  CodeSnippetInsertionPayload,
  CodeSnippetSupportingReceiver,
} from '../types';
import { codeTypeFiltersForDropZone } from '../utils/snippetExecutionModel';

interface CodeEmbedProps {
  id: string;
  testId?: string;
  code: string;
  insertText?: string;
  title: string;
  draggableId?: string;
  type: string | undefined;
  dropZone?: CodeSnippetDropZoneMode;
  codeByDropZone?: CodeSnippetCodeByDropZone;
  imports?: CodeSnippetImport[];
  frameworkClassName?: string;
  display?: string;
  receiverClassName?: string;
  supportingReceiver?: CodeSnippetSupportingReceiver;
  disabled?: boolean;
  disabledReason?: string;
  activationInstructions: string;
  onActivate?: (payload: CodeSnippetInsertionPayload) => CodeSnippetInsertionOutcome;
  executionModelLabels?: Record<CodeSnippetDropZone, string>;
  manageKeyboardNavigation?: boolean;
}

const normalizeSnippetBody = (code: string): string =>
  code
    .split('\n')
    .filter((line) => line !== 'from sphero import *')
    // Collapse repeated blank lines to save space. Two blank lines after imports
    // is conventional but a big waste of space here.
    .filter((line, index, array) => index === 0 || !(line.length === 0 && array[index - 1].length === 0))
    .join('\n')
    .trim();

const CodeEmbed = ({
  id,
  testId,
  code: codeWithImports,
  insertText,
  title,
  draggableId,
  type,
  dropZone,
  codeByDropZone,
  imports,
  frameworkClassName,
  display,
  receiverClassName,
  supportingReceiver,
  disabled = false,
  disabledReason,
  activationInstructions,
  onActivate,
  executionModelLabels,
  manageKeyboardNavigation = false,
}: CodeEmbedProps) => {
  const concise = useMemo(() => normalizeSnippetBody(codeWithImports), [codeWithImports]);
  const preview = display !== undefined ? display : concise;
  const executionModels = executionModelLabels ? codeTypeFiltersForDropZone(dropZone) : [];

  const payload = useMemo<CodeSnippetInsertionPayload>(
    () => ({
      id,
      title,
      code: concise,
      insertText,
      type,
      dropZone,
      codeByDropZone,
      imports,
      frameworkClassName,
      receiverClassName,
      supportingReceiver,
    }),
    [codeByDropZone, concise, dropZone, frameworkClassName, id, imports, insertText, receiverClassName, supportingReceiver, title, type]
  );
  const { attributes, isDragging, listeners, setNodeRef } = useDraggable({
    id: draggableId ?? id,
    data: payload,
    disabled,
  });
  const dragAttributes = disabled ? {} : attributes;
  const dragListeners = disabled ? {} : listeners;
  const suppressActivationRef = useRef(false);
  const suppressActivationTimerRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);
  const descriptionId = `${id}-description`;
  const instructionsId = `${id}-instructions`;

  useEffect(() => {
    if (isDragging) {
      suppressActivationRef.current = true;
      if (suppressActivationTimerRef.current) {
        globalThis.clearTimeout(suppressActivationTimerRef.current);
        suppressActivationTimerRef.current = null;
      }
      return;
    }

    if (suppressActivationRef.current) {
      suppressActivationTimerRef.current = globalThis.setTimeout(() => {
        suppressActivationRef.current = false;
        suppressActivationTimerRef.current = null;
      }, 0);
    }

    return () => {
      if (suppressActivationTimerRef.current) {
        globalThis.clearTimeout(suppressActivationTimerRef.current);
      }
    };
  }, [isDragging]);

  const activate = (target: HTMLButtonElement) => {
    if (disabled || !onActivate || isDragging || suppressActivationRef.current) return;

    const outcome = onActivate(payload);
    if (outcome === 'rejected') {
      target.focus();
    }
  };

  const snippetContent = (
    <Stack
      direction="row"
      alignItems={'center'}
      width={'100%'}
      minWidth={0}
      sx={{ cursor: disabled ? 'not-allowed' : 'grab' }}
      justifyContent={'flex-start'}
    >
      <Box display={'flex'} flexShrink={0}>
        <DragIndicatorIcon color={disabled ? 'disabled' : 'inherit'} />
      </Box>

      <CodeSnippetView id={descriptionId} value={preview} />
      {onActivate ? (
        <Box
          id={instructionsId}
          component="span"
          sx={{
            border: 0,
            clip: 'rect(0 0 0 0)',
            height: 1,
            margin: -1,
            overflow: 'hidden',
            padding: 0,
            position: 'absolute',
            whiteSpace: 'nowrap',
            width: 1,
          }}
        >
          {disabledReason ?? activationInstructions}
        </Box>
      ) : null}
    </Stack>
  );

  return (
    <Card
      data-testid={executionModelLabels ? 'contextual-code-snippet-card' : undefined}
      aria-disabled={disabled ? true : undefined}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        border: 1,
        borderColor: disabled ? 'action.disabled' : 'divider',
        borderStyle: disabled ? 'dashed' : 'solid',
        m: 0,
        p: 0,
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          width: '100%',
          ...(disabled
            ? {}
            : {
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
              }),
        }}
      >
        <CardContent
          sx={{
            p: 0,
            '&:last-child': { pb: 0 },
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {onActivate ? (
            <ButtonBase
              id={id}
              data-testid={testId}
              ref={setNodeRef}
              {...dragAttributes}
              {...dragListeners}
              type="button"
              tabIndex={manageKeyboardNavigation ? -1 : undefined}
              data-snippet-navigation-item={manageKeyboardNavigation ? 'true' : undefined}
              disabled={disabled}
              aria-label={title}
              aria-describedby={`${descriptionId} ${instructionsId}`}
              onClick={(event) => activate(event.currentTarget)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                activate(event.currentTarget);
              }}
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                textAlign: 'left',
                '&.Mui-focusVisible': {
                  outline: '3px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: '-3px',
                },
              }}
            >
              {snippetContent}
            </ButtonBase>
          ) : (
            <Box id={id} data-testid={testId} ref={setNodeRef} {...dragAttributes} {...dragListeners} sx={{ width: '100%' }}>
              {snippetContent}
            </Box>
          )}
          {executionModels.length > 0 ? (
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ px: 1, pb: 0.75 }}>
              {executionModels.map((executionModel) => (
                <Chip
                  key={executionModel}
                  data-testid="snippet-execution-model"
                  data-code-type={executionModel}
                  label={executionModelLabels?.[executionModel]}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: 'divider',
                    color: 'text.secondary',
                    height: 20,
                    '& .MuiChip-label': {
                      fontSize: 10,
                      fontWeight: 700,
                      px: 0.75,
                    },
                  }}
                />
              ))}
            </Stack>
          ) : null}
        </CardContent>
      </Box>
    </Card>
  );
};

export default CodeEmbed;
